import React from 'react';
import {ReferralPageAction} from 'placement/reducers/referralPage';
import {Referral} from 'placement/domains';
import ClearIcon from '@material-ui/icons/Clear';
import {EstimatedServiceHoursSection} from './EstimatedServiceHoursSection';
import {SiblingDetailsAllProvider} from "./SiblingDetailsAllProvider";
import {ServiceLocationEnumNameToValue} from "../const";
import {FatGreenOutlineButton} from "../../common";
import {useHistory} from "react-router-dom";
import {getClientPlacementRootUrl} from "../routes";

type Props = {
    showModal: boolean;
    referral: Referral | null;
    dispatch: React.Dispatch<ReferralPageAction>
}

export function ReferralDetailModal({
                                        showModal, referral, dispatch
                                    }: Props) {
    const hideModal = () => dispatch({type: 'SET_HIDE_MODAL'});

    const escFunction = React.useCallback((event) => {
        if (event.keyCode === 27) {
            dispatch({type: 'SET_HIDE_MODAL'})
        }
    }, [dispatch]);

    React.useEffect(() => {
        document.addEventListener("keydown", escFunction, false);

        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    // for transition animation
    const transformCss = showModal ? 'translate-x-0 shadow-3xl' : 'translate-x-full';

    return (
        <div className={`fixed top-0
                            ml-auto
                            flex flex-col
                            inset-0 border-0 rounded-2xl
                            overflow-y-scroll scrollbar-always
                            z-40
                            w-full
                            bg-white
                            transition-transform duration-300 ease-in-out
                            transform
                            outline-none focus:outline-none ${transformCss}`}
             style={{top: '5rem', width: '420px'}}
        >
            < div className='border-0 overflow-y-scroll scrollbar-always relative flex flex-col h-full w-full bg-white'>
                < div className='flex items-start justify-between p-5 rounded-t'>
                    <h3 className='text-xl font-semibold'>
                        Referral Details
                    </h3>
                    <button
                        className='bg-transparent outline-none focus:outline-none'
                        onClick={hideModal}
                    >
                        <div
                            className='bg-transparent text-atlantis-200 opacity-5 h-6 w-6 m-auto outline-none focus:outline-none'>
                            <ClearIcon/>
                        </div>
                    </button>
                </div>
                <div className='pl-5 flex-auto'>
                    <div className='pb-5'>
                        <div className='flex pb-2'>
                            <div className='w-1/2'>
                                Referral ID
                            </div>
                            <a href={referral?.salesforceCaseUrl ?? ''} target="_blank" rel="noopener noreferrer">
                                <div className='w-1/2 underline text-blue-portal-link'>
                                    {referral?.caseNumber ? referral.caseNumber : 'None'}
                                </div>
                            </a>
                        </div>
                        <div className='flex pb-2'>
                            <div className='w-1/2'>
                                Client Name
                            </div>
                            <div className='w-1/2'>
                                {referral?.clientName ? referral.clientName : 'None'}
                            </div>
                        </div>
                        <div className='flex pb-2'>
                            <div className='w-1/2'>
                                Age
                            </div>
                            <div className='w-1/2'>
                                {referral?.clientAge ? referral.clientAge : 'None'}
                            </div>
                        </div>
                        <div className='flex pb-2'>
                            <div className='w-1/2'>
                                Scope of Services
                            </div>
                            <div className='w-1/2'>
                                {referral?.placementType ? referral.placementType : 'None'}
                            </div>
                        </div>
                        <div className='flex pb-2'>
                            <div className='w-1/2'>
                                Service Type
                            </div>
                            <div className='w-1/2'>
                                {referral?.serviceTypes && referral.serviceTypes.length > 0 ?
                                    referral?.serviceTypes.map((serviceType) => <p
                                        key={serviceType}>{serviceType}</p>) : 'None'}
                            </div>
                        </div>
                        <div className='flex pb-2'>
                            <div className='w-1/2'>
                                Location
                            </div>
                            <div className='w-1/2'>
                                {referral?.location ? referral.location : 'None'}
                            </div>
                        </div>
                        <div className='flex pb-2'>
                            <div className='w-1/2'>
                                Days in Placement
                            </div>
                            <div className='w-1/2'>
                                {referral?.daysInPlacement ? referral?.daysInPlacement : 'None'}
                            </div>
                        </div>
                        <div className='flex pb-2'>
                            <div className='w-1/2'>
                                Escalation Level
                            </div>
                            <div className='w-1/2'>
                                {referral?.escalationLevel ? referral?.escalationLevel : 'None'}
                            </div>
                        </div>
                        <div className='flex pb-2'>
                            <div className='w-1/2'>
                                Required Language
                            </div>
                            <div className='w-1/2'>
                                {referral?.requiredLanguage ? referral.requiredLanguage : 'None'}
                            </div>
                        </div>
                        <SiblingDetailsAllProvider referral={referral}/>
                    </div>

                    <EstimatedServiceHoursSection referral={referral!}/>
                    {
                        referral?.serviceLocations && referral.serviceLocations.length > 0 &&
                        <div className='pb-5'>
                            <div className='font-semibold pb-5'>
                                Service Location
                            </div>
                            <ul className='pl-5'>
                                {
                                    referral?.serviceLocations
                                        .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                        .filter((v) => v !== undefined)
                                        .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                                }
                            </ul>
                        </div>
                    }
                    {
                        referral?.serviceLocationsParaprofessional && referral.serviceLocationsParaprofessional.length > 0 &&
                        <div className='pb-4'>
                            <div className='font-semibold pb-5'>
                                Service Location Paraprofessional
                            </div>
                            <ul className='pl-5'>
                                {
                                    referral?.serviceLocationsParaprofessional
                                        .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                        .filter((v) => v !== undefined)
                                        .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                                }
                            </ul>
                        </div>
                    }
                    {
                        referral?.serviceLocationsMidLevel && referral.serviceLocationsMidLevel.length > 0 &&
                        <div className='pb-4'>
                            <div className='font-semibold pb-5'>
                                Service Location Mid-Level
                            </div>
                            <ul className='pl-5'>
                                {
                                    referral?.serviceLocationsMidLevel
                                        .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                        .filter((v) => v !== undefined)
                                        .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                                }
                            </ul>
                        </div>
                    }
                    {
                        referral?.serviceLocationsHighLevel && referral.serviceLocationsHighLevel.length > 0 &&
                        <div className='pb-4'>
                            <div className='font-semibold pb-5'>
                                Service Location High-Level
                            </div>
                            <ul className='pl-5'>
                                {
                                    referral?.serviceLocationsHighLevel
                                        .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                        .filter((v) => v !== undefined)
                                        .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                                }
                            </ul>
                        </div>
                    }
                    {
                        referral?.serviceLocationsGroupCare && referral.serviceLocationsGroupCare.length > 0 &&
                        <div className='pb-4'>
                            <div className='font-semibold pb-5'>
                                Service Location Group Care
                            </div>
                            <ul className='pl-5'>
                                {
                                    referral?.serviceLocationsGroupCare
                                        .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                        .filter((v) => v !== undefined)
                                        .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                                }
                            </ul>
                        </div>
                    }

                    <div className='pb-5'>
                        <div className='font-semibold pb-5'>
                            Schedule Notes
                        </div>
                        <div className='pb-2'>
                            {referral?.scheduleNotes ? referral?.scheduleNotes.split('\n').map((i, key) => {
                                return <div className='pb-2' key={key}>{i}</div>;
                            }) : 'None'}
                        </div>
                    </div>
                    <div className='pb-5'>
                        <div className='font-semibold pb-5'>
                            Treatment Considerations
                        </div>
                        {referral?.referralConsiderations && referral.referralConsiderations.length > 0
                            ? referral?.referralConsiderations
                                .sort((a, b) => a.localeCompare(b))
                                .map((i, key) => <div className='pb-2' key={key}>{i}</div>)
                            : 'None'
                        }
                    </div>
                </div>
            </div>
            {referral?.guid && <SubmitViewOfferButton referralGuid={referral.guid}/>}
        </div>);
}

const SubmitViewOfferButton = ({referralGuid}: { referralGuid: string }) => {
    const history = useHistory();
    const rootUrl = getClientPlacementRootUrl(history.location.pathname);

    return (
        <div className="p-5 w-full sticky bottom-0 bg-white">
            <FatGreenOutlineButton
                text='Submit Offer'
                onClick={() => history.push(`${rootUrl}/submit-offer/${referralGuid}`)}
            />
        </div>
    )
}
